import { t } from "../../i18n";
import { Check, LoaderCircle, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../../api";
import { SettingsPage } from "./ProfilePage";

type GeneralSettings = { defaultLanguage: "en" | "fr" };
export function GeneralSettingsPage() {
  const [defaultLanguage, setDefaultLanguage] = useState<"en" | "fr">("en");
  const [busy, setBusy] = useState(true);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    void api<GeneralSettings>("/admin/general-settings")
      .then((settings) => setDefaultLanguage(settings.defaultLanguage))
      .catch((reason) => setError(reason.message))
      .finally(() => setBusy(false));
  }, []);
  const save = async () => {
    setBusy(true);
    setSaved(false);
    setError("");
    try {
      const settings = await api<GeneralSettings>("/admin/general-settings", {
        method: "PATCH",
        body: JSON.stringify({ defaultLanguage }),
      });
      setDefaultLanguage(settings.defaultLanguage);
      setSaved(true);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : t("General settings could not be saved"),
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <SettingsPage
      title={t("General")}
      description={t("Configure website-wide defaults for your organization.")}
    >
      <section className="panel max-w-2xl p-5 sm:p-6">
        <label>
          <span className="label">{t("Website default language")}</span>
          <select
            className="field"
            disabled={busy}
            value={defaultLanguage}
            onChange={(event) => {
              setDefaultLanguage(event.target.value as "en" | "fr");
              setSaved(false);
            }}
          >
            <option value="en">English</option>
            <option value="fr">Français</option>
          </select>
          <span className="mt-2 block text-xs text-stone-400">
            {t("Used when a user has not chosen a personal language.")}
          </span>
        </label>
        {error && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-300">{error}</p>
        )}
        <div className="mt-5 flex justify-end">
          <button
            className="button-primary"
            disabled={busy}
            onClick={() => void save()}
          >
            {busy ? (
              <LoaderCircle className="animate-spin" size={16} />
            ) : saved ? (
              <Check size={16} />
            ) : (
              <Save size={16} />
            )}{" "}
            {saved ? t("Saved") : t("Save")}
          </button>
        </div>
      </section>
    </SettingsPage>
  );
}
